import * as React from "react";
import { Link } from "gatsby";
import {
  AskAboutQuery_OwidCountry,
  AskAboutQuery_OwidCase,
} from "../../../../../../graphql-types";
import {
  getCountryUrl,
  getCOVIDIntentUrl,
} from "../../../../../utils/question-url";
import {
  getEventAttributes,
  EventCategory,
} from "../../../../../utils/analytics";
import DailyCasesChartTiny from "../../../../ui-library/daily-cases-chart/daily-cases-chart-tiny";
import { getPerformanceByCases } from "../../../../../utils/get-country-performance";
import { formatNumberDisplay } from "../../../../../utils/format-number";
import Image from "../../../../ui-library/image/image";
import { Colors } from "../../../../design-tokens/colors";
import styles from "./country-situation-table.styles";
import GlobeIcon from "./assets/mini-glob-icon.svg";
import { SPECIAL_INTENTS } from "../../../../../constants/special-intents";

export type CountryData = Pick<
  AskAboutQuery_OwidCountry,
  "iso2" | "alias" | "displayName" | "population"
> & {
  latest: Pick<
    AskAboutQuery_OwidCase,
    | "date"
    | "totalCases"
    | "totalCasesPerMillion"
    | "totalDeaths"
    | "averageNewCases"
    | "infectionRate"
  >;
  data: Array<
    Pick<AskAboutQuery_OwidCase, "date" | "averageNewCases" | "infectionRate">
  >;
};

interface CountrySituationTableRowProps {
  countryData: CountryData;
}

const CountrySituationTableRow: React.FC<CountrySituationTableRowProps> = ({
  countryData,
}) => {
  const { color } = getPerformanceByCases({
    countryName: countryData.displayName,
    latest: countryData.latest,
    data: countryData.data,
  });

  const url =
    countryData.displayName === "World"
      ? getCOVIDIntentUrl(SPECIAL_INTENTS.GlobalSituation.alias)
      : getCountryUrl(countryData.alias);

  return (
    <Link
      to={url}
      {...getEventAttributes({
        category: EventCategory.CountryControlCompareTable,
        action: "Click - Country name",
        label: countryData.displayName,
      })}
      css={[styles.row, styles.bodyRow]}
    >
      <div>
        <span css={styles.flag}>
          {countryData.displayName === "World" ? (
            <Image alt="World" src={GlobeIcon} />
          ) : (
            <Image
              alt={countryData.displayName}
              src={`https://catamphetamine.gitlab.io/country-flag-icons/1x1/${countryData.iso2}.svg`}
            />
          )}
        </span>
        <span css={styles.label}>
          {countryData.displayName === "World"
            ? "Global situation"
            : countryData.displayName}
        </span>
      </div>
      <div>{formatNumberDisplay(countryData.latest.totalCases || 0)}</div>
      <div css={{ color: Colors.Red }}>
        {countryData.latest.totalDeaths !== null &&
        countryData.latest.totalDeaths !== undefined
          ? formatNumberDisplay(countryData.latest.totalDeaths)
          : "-"}
      </div>
      <div>{formatNumberDisplay(countryData.latest.averageNewCases)}</div>
      <div>
        <DailyCasesChartTiny
          data={countryData.data}
          dataKey="averageNewCases"
          color={color}
          width={60}
          height={40}
        />
      </div>
    </Link>
  );
};

export default CountrySituationTableRow;
